import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback } from '@theme/utilities';

export default class BlogPostsList extends PaginatedList {
  pages = new Map();
  #resolveNextPagePromise = null;
  #resolvePreviousPagePromise = null;

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('initialized', '');

    const { loadMore } = this.refs;

    const params = new URLSearchParams(window.location.search);
    const sectionParam = params.get('section');
    const sectionId = this.getAttribute('section-id');

    const currentPageFromURL = Number(params.get('page') || 1);
    const currentPage = sectionParam === sectionId ? currentPageFromURL : 1;

    if (currentPage > 1) {
      this.#restorePreviousPages(currentPage);
    }

    this.#fetchPage('next');

    if (loadMore) {
      loadMore.addEventListener('click', this.loadMore);
    }

    this.#toggleLoadMoreVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { loadMore } = this.refs;
    if (loadMore) loadMore.removeEventListener('click', this.loadMore);
  }

  async #restorePreviousPages(currentPage) {
    const { grid } = this.refs;
    if (!grid) return;

    for (let page = currentPage - 1; page >= 1; page--) {
      await this.#fetchSpecificPage(page);

      const pageItems = this.#getGridForPage(page);
      if (!pageItems?.length) continue;

      grid.prepend(...pageItems);
    }
  }

  #toggleLoadMoreVisibility() {
    const { grid, cards, loadMore } = this.refs;
    if (!grid || !loadMore || !cards?.length) return;

    const lastPage = Number(grid.dataset.lastPage);
    const currentPage = Number(cards[cards.length - 1].dataset.page);

    if (currentPage >= lastPage) loadMore.remove();
  }

  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;
    return true;
  }

  async #fetchPage(type) {
    const page = this.#getPage(type);
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.searchParams.set('section', this.getAttribute('section-id')); 
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;
    if (!grid) return;

    const nextPage = this.#getPage('next');
    if (!nextPage || !this.#shouldUsePage(nextPage)) return;

    let nextPageItemElements = this.#getGridForPage(nextPage.page);
    if (!nextPageItemElements) {
      const promise = new Promise(res => this.#resolveNextPagePromise = res);
      await this.#fetchPage('next');
      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);
    history.pushState('', '', nextPage.url.toString());
    requestIdleCallback(() => this.#fetchPage('next'));
  }

  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];
    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.searchParams.set('section', this.getAttribute('section-id')); // 🔹 section param
    url.hash = '';

    return { page, url };
  }

  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);
    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;

    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  #delay(ms = 150) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  loadMore = async () => {
    const { loadMore } = this.refs;

    loadMore?.setAttribute('disabled', 'disabled');
    loadMore?.setAttribute('aria-disabled', 'true');
    loadMore?.classList.add('loading');

    await this.#renderNextPage();
    this.#toggleLoadMoreVisibility();

    setTimeout(() => {
      loadMore?.removeAttribute('disabled');
      loadMore?.setAttribute('aria-disabled', 'false');
      loadMore?.classList.remove('loading');
    }, 300);
  };
}

if (!customElements.get('blog-posts-list')) {
  customElements.define('blog-posts-list', BlogPostsList);
}
